/**
 * The link between a job and its joining form (decision 101): `/onboarding?requisitionId=<id>&requisitionDisplayId=<display
 * id>`. The BGV forms API keeps one form per requisition and a save names it by both ids, so the URL carries both and a
 * refresh opens the same form. Light on purpose, so the My Jobs button can use it without the route's imports.
 */
import { ROUTES } from '@/app/router/routes';
import type { RequisitionIds } from '../../index';

/** The query parameters that name the requisition (job) the form is for */
export const REQUISITION_ID_PARAM = 'requisitionId';
export const REQUISITION_DISPLAY_ID_PARAM = 'requisitionDisplayId';

/**
 * An id as the BGV service takes it (letters, digits, `_` and `-`, at most 128); anything else is ignored, so a crafted
 * link can't reach another API path.
 */
function idFrom(value: string | null | undefined): string | undefined {
  const id = value?.trim() ?? '';
  return /^[A-Za-z0-9_-]{1,128}$/.test(id) ? id : undefined;
}

/** Both ids of the requisition; `undefined` when either is missing or doesn't look like an id */
export function requisitionFrom(requisitionId: string | null | undefined, requisitionDisplayId: string | null | undefined): RequisitionIds | undefined {
  const id = idFrom(requisitionId);
  const displayId = idFrom(requisitionDisplayId);
  return id === undefined || displayId === undefined ? undefined : { requisitionId: id, requisitionDisplayId: displayId };
}

/** The requisition the page's query names */
export function requisitionOf(search: URLSearchParams): RequisitionIds | undefined {
  return requisitionFrom(search.get(REQUISITION_ID_PARAM), search.get(REQUISITION_DISPLAY_ID_PARAM));
}

/** The form's address for a job; `undefined` without both ids the route would read */
export function onboardingPathFor(requisitionId: string | null | undefined, requisitionDisplayId: string | null | undefined): string | undefined {
  const requisition = requisitionFrom(requisitionId, requisitionDisplayId);
  if (!requisition) return undefined;
  const query = new URLSearchParams({ [REQUISITION_ID_PARAM]: requisition.requisitionId, [REQUISITION_DISPLAY_ID_PARAM]: requisition.requisitionDisplayId });
  return `${ROUTES.ONBOARDING}?${query}`;
}
